import React, { useState } from "react";
import { SERVER_URL } from "../utils/api";
import ProfileModal from "./ProfileView";
import RideDetailsDriver from "./RideDetailsDriver";
import RideDetailsPassenger from "./RideDetailsPassenger";
import "../css/ridecard.css"; 

export default function RideDetailsCard({ ride, user, onRideUpdate }) {
  const [showProfile, setShowProfile] = useState(false);
  
  // --- Derived Data Logic ---
  const driver = ride?.postedBy || ride?.driver || {};
  const driverId = driver?._id || driver?.id; 
  const userId = user?._id || user?.id;
  
  const isDriver = String(userId) === String(driverId);
  const isFull = ride.seatsAvailable === 0;
  
  const driverRating = Number(driver.rating) || 0;
  const driverTotalRatings = Number(driver.totalRatings) || 0;

  const avatar = driver.avatar 
    ? (driver.avatar.startsWith("http") ? driver.avatar : `${SERVER_URL}${driver.avatar}`)
    : "/default-avatar.png";

  const dateObj = new Date(ride.date || Date.now());
  const dateStr = dateObj.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'short' });

  // App-Style Vehicle Logic
  const vehicleStr = ride.vehicle || "Standard Vehicle (Car)";
  const vehicleMatch = vehicleStr.match(/(.*) \((.*)\)/);
  const modelName = vehicleMatch ? vehicleMatch[1].trim() : vehicleStr;
  const typeName = vehicleMatch ? vehicleMatch[2].trim() : "Car";
  const vIcon = typeName.toLowerCase().includes("bike") ? "🏍️" : typeName.toLowerCase().includes("auto") ? "🛺" : "🚗";

  const reviews = ride.ratings || ride.reviews || ride.feedbacks || [];

  return (
    <div className="rd-wrapper">
      <div className="rd-container">
        
        {/* 1. HEADER */}
        <div className="rd-header">
          <h1 className="rd-date">{dateStr}</h1>
          <div className="rd-tags">
            {ride.status === 'cancelled' && <span className="rd-badge cancel">Cancelled</span>}
            {ride.status === 'completed' && <span className="rd-badge complete">Completed</span>}
          </div>
        </div>

        {/* 2. PRICE & TIMELINE CARD */}
        <div className="rd-card">
          <div className="rd-price-row">
            <span className="label">Total Price</span>
            <span className="value">₹{ride.pricePerSeat}</span>
          </div>
          <div className="rd-divider"></div>
          <div className="rd-timeline">
            <div className="rd-stop">
              <span className="rd-time">{ride.time}</span>
              <div className="rd-visual"><div className="rd-dot start"></div><div className="rd-line"></div></div>
              <div className="rd-loc"><h3>{ride.from}</h3><p>Pick-up point</p></div>
            </div>
            <div className="rd-stop">
              <span className="rd-time faded">--:--</span>
              <div className="rd-visual"><div className="rd-dot end"></div></div>
              <div className="rd-loc"><h3>{ride.to}</h3><p>Drop-off point</p></div>
            </div>
          </div>
        </div>

        {/* 3. VEHICLE & SEATS */}
        <div className="rd-split-card">
          <div className="rd-split-part">
            <div className="rd-icon-circle">{vIcon}</div>
            <div className="rd-split-text">
              <span className="sub">Vehicle Info</span>
              <span className="main">{modelName}</span>
              <span className="badge">{typeName}</span>
            </div>
          </div>
          <div className="rd-vertical-divider"></div>
          <div className="rd-split-part">
            <div className="rd-icon-circle">{isFull ? "⛔" : "💺"}</div>
            <div className="rd-split-text">
              <span className="sub">Available</span>
              <span className={`main ${isFull ? "red" : "green"}`}>
                {isFull ? "Full" : `${ride.seatsAvailable} Seats`}
              </span>
              <span className="badge">Confirm</span>
            </div>
          </div>
        </div>

        {/* 4. DRIVER INFO (CLICKABLE FOR PROFILE) */}
        <div className="rd-card clickable" onClick={() => setShowProfile(true)}>
          <div className="rd-driver-row">
            <img src={avatar} alt="driver" className="rd-avatar" />
            <div className="rd-driver-info">
              <div className="name-row">
                <h3>{driver.name}</h3>
                {driver.verified && <span className="verified">✔</span>}
              </div>
              <div className="rating-row">
                <span className="star">★</span> {driverRating.toFixed(1)} 
                <span className="count">({driverTotalRatings} ratings)</span>
              </div>
            </div>
            <div className="rd-chevron">›</div>
          </div>
          {ride.description && (
            <div className="rd-note-box"><p>"{ride.description}"</p></div>
          )}
        </div>

        {/* 5. REVIEWS */}
        {reviews.length > 0 && (
          <div className="rd-card">
            <h3 className="rd-section-title">Ride Reviews</h3>
            <div className="rd-reviews-list">
              {reviews.map((rating, idx) => {
                const reviewer = rating.user || rating.from || {};
                const reviewerAvatar = reviewer.avatar
                  ? (reviewer.avatar.startsWith("http") ? reviewer.avatar : `${SERVER_URL}${reviewer.avatar}`)
                  : "/default-avatar.png";
                return (
                  <div key={idx} className="rd-review-item">
                    <div className="rd-review-header">
                      <img src={reviewerAvatar} alt="reviewer" className="rd-reviewer-avatar" />
                      <div className="rd-reviewer-info">
                        <h4>{reviewer.name || "User"}</h4>
                        <div className="rd-stars">
                          {[...Array(5)].map((_, i) => (
                            <span key={i} className={i < rating.rating ? "star filled" : "star empty"}>★</span>
                          ))}
                        </div>
                      </div>
                      <div className="rd-rating-badge">{rating.rating}.0</div>
                    </div>
                    {rating.review && <p className="rd-review-text">"{rating.review}"</p>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 6. ACTIONS SECTION (DYNAMIC IMPORT) */}
        <div className="rd-action-section">
          {isDriver ? (
            <RideDetailsDriver ride={ride} user={user} onRideUpdate={onRideUpdate} />
          ) : (
            <RideDetailsPassenger ride={ride} user={user} driver={driver} />
          )}
        </div>

      </div>

      {/* ✅ PROFILE VIEW MODAL IS SECURELY ATTACHED HERE */}
      {showProfile && driverId && (
        <ProfileModal userId={driverId} onClose={() => setShowProfile(false)} />
      )}
    </div>
  );
}